import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getIndexName } from '../constants';
import { createBrowserSearchClient } from '../utils/search/create-browser-search-client';
import type { BrowserSearchClient } from '../utils/search/create-browser-search-client';
import {
	generateUrlFromIndexUiState,
	parseUrlToIndexUiState,
	pathnameIsJobsListingPath,
	type GenerateUrlFromIndexUiStateOptions,
} from '../utils/search-router/url-handling';
import { refinementKeyToAlgoliaNumericAttribute } from '../utils/search-router/refinement-key-to-search-numeric-attribute';
import {
	readPrimaryLocationFilterValues,
	reorderSearchHitsForPrimaryLocationBoost,
} from '../utils/location/reorder-search-hits-for-primary-location-boost';
import { usePageContext } from 'vike-react/usePageContext';
import { extractFacetsFromSearchResults, FACET_ATTRIBUTES, type FacetsData } from '../utils/facets/facets-shared';
import { trackSearchQuery, trackFilterAdd, trackFilterRemove, trackSortChange, clearQueryID, trackEvent } from '../utils/analytics/analytics';
import {
	buildSearchFiltersJson,
	resolveSearchSortLabel,
	trackSearchAnalyticsGa4Debounced,
} from '../utils/analytics/track-search-analytics-ga4';
import { isSrpNonFilterQueryParam } from '../utils/search/srp-non-filter-query-params';
import { useAppSelector, useAppDispatch, useRehydrated } from '../redux/hooks';
import { useSelector, shallowEqual } from 'react-redux';
import type { RootState } from '../redux/createStore';
import { isToolsPage } from '../utils/routing/route-utils';
import { addSearch, refineSearch } from '../redux/features/savedSearchesSlice';
import {
	normalizeSavedSearchUrlInput,
	shouldSkipPersistingSavedSearchUrl,
} from '../utils/saved-searches/normalize-saved-search-url';
import { setFinlandFilterEnabled } from '../redux/features/finlandFilterSlice';
import { shouldIgnoreEmptyInitialFiltersFromLayout } from '../utils/search/should-ignore-empty-initial-filters-from-layout';
import { isExpectedEnvironmentError } from '../utils/shared/is-expected-environment-error';
import { hasExplicitNonFinlandSelection, buildFinlandBaselineFacetFilter, swapCompoundFinlandFilterForFallback } from '../utils/geocoding/finland-place-ids';
import { useNewLocationSystemEnabled } from '../utils/facets/new-location-system-dev-flag';
import { SRP_HITS_PER_PAGE, isValidSrpHitsPerPage } from '../utils/srp/srp-hits-per-page';
import { SRP_MAP_SSR_GEO_HITS_PER_PAGE } from '../utils/srp/srp-map-ssr-geo-hits-limit';
import { setClientPreferenceCookie } from '../utils/shared/client-preferences';
import { computeSearchPageTitleFromLiveState } from '../pages/index/utils/seoUtils';
import { MAP_TRENDING_RECENT_WINDOW_DAYS } from '../utils/srp/map-trending-recent-window';

/**
 * True when the active filters explicitly select at least one non-Finland location (e.g. a
 * foreign city hub page) OR a non-Finland `regionTags` value (TEC-482, a continent, a recruiting
 * region like "Nordics"/"EMEA" that includes countries other than Finland, or any other country;
 * a Finland city/admin1/the Finland country id itself doesn't count) OR an explicit
 * `remoteEligibleCountryCodes` filter (TEC-482 follow-up: the "N more remote roles" browse view
 * deliberately filters on remote eligibility instead of physical location, and those jobs aren't
 * physically in Finland by definition). ANDing isFinland:true on top of any of these is
 * self-contradictory and would always produce zero results, so callers should skip the isFinland
 * facet filter in that case.
 */
export function hasExplicitNonFinlandLocationFilter(filters: unknown): boolean {
	if (!filters || typeof filters !== 'object') return false;
	const f = filters as Record<string, unknown>;

	const rawLocation = f.location;
	const locationValues = Array.isArray(rawLocation) ? rawLocation : rawLocation !== undefined ? [rawLocation] : [];

	const rawRegionTags = f.regionTags;
	const regionTagsValues = Array.isArray(rawRegionTags) ? rawRegionTags : rawRegionTags !== undefined ? [rawRegionTags] : [];

	if (hasExplicitNonFinlandSelection(locationValues.map(String), regionTagsValues.map(String))) {
		return true;
	}

	const rawRemoteEligibleCountryCodes = f.remoteEligibleCountryCodes;
	const remoteEligibleCountryCodesValues = Array.isArray(rawRemoteEligibleCountryCodes)
		? rawRemoteEligibleCountryCodes
		: rawRemoteEligibleCountryCodes !== undefined
			? [rawRemoteEligibleCountryCodes]
			: [];
	return remoteEligibleCountryCodesValues.length > 0;
}

/**
 * TEC-482 follow-up: `client.search(requests)`, but retried once with the plain `isFinland:true`
 * fallback if the request fails while using the compound Finland-baseline filter (physical +
 * remote-eligible). Covers the window where `dev_enable_new_location_system` is on but the
 * `remoteEligibleCountryCodes` schema migration hasn't deployed yet — Typesense hard-errors an
 * OR referencing an unknown facet field instead of treating it as a graceful zero-match side,
 * so without this retry that combination breaks the whole search instead of just degrading to
 * the old behavior. See swapCompoundFinlandFilterForFallback's own doc comment for the concrete
 * error this guards against (confirmed live, not hypothetical).
 */
async function searchWithFinlandBaselineFallback(
	client: BrowserSearchClient,
	requests: Parameters<BrowserSearchClient['search']>[0],
	newLocationSystemEnabled: boolean,
): ReturnType<BrowserSearchClient['search']> {
	try {
		return await client.search(requests);
	} catch (error) {
		if (!newLocationSystemEnabled) throw error;
		const fallbackRequests = requests.map((req) => ({
			...req,
			params: { ...req.params, facetFilters: swapCompoundFinlandFilterForFallback((req.params as any)?.facetFilters) },
		}));
		return client.search(fallbackRequests);
	}
}

type AlgoliaFilters = string | Record<string, unknown> | undefined;

const IS_SEARCH_CONTEXT_DEBUG_ENABLED = import.meta.env.DEV;

function debugLog(...args: unknown[]): void {
	if (!IS_SEARCH_CONTEXT_DEBUG_ENABLED) return;
	console.warn(...args);
}

function urlSyncOptionsForPathname(pathname: string): GenerateUrlFromIndexUiStateOptions {
	return { forceJobsListingForCompany: pathnameIsJobsListingPath(pathname) };
}

// Helper to create stable string representation of filters for comparison
function stableStringify(value: unknown): string {
	if (value === null || typeof value !== 'object') return JSON.stringify(value);
	if (Array.isArray(value)) return JSON.stringify(value.map(stableStringify));
	const obj = value as Record<string, unknown>;
	const keys = Object.keys(obj).sort();
	const normalized: Record<string, unknown> = {};
	for (const k of keys) normalized[k] = obj[k];
	return JSON.stringify(normalized);
}

function filtersKey(f: AlgoliaFilters): string {
	if (typeof f === 'string') return `s:${f}`;
	if (!f) return 'u:';
	return `o:${stableStringify(f)}`;
}

interface SearchProviderProps {
	children: React.ReactNode;
	indexName: string;
	initialQuery?: string;
	initialFilters?: AlgoliaFilters;
	initialPage?: number;
	/** Results per page (see srp-hits-per-page.ts). Defaults to SRP_HITS_PER_PAGE when omitted. */
	initialPerPage?: number;
	initialResults?: any; // Raw Algolia results JSON (server-fetched)
	syncToUrl?: boolean; // if true, push URL on state changes
	initialSort?: 'relevance' | 'date' | 'salary';
	/** When outside Vike tree (e.g. SRP island), pass locale so SearchProvider does not rely on usePageContext(). */
	localeOverride?: string;
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface SearchContextValue {
	// Removed indexName - not used by any consumer (they use getIndexName directly)
	query: string;
	setQuery: (q: string) => void;
	filters: AlgoliaFilters;
	setFilters: (f: AlgoliaFilters) => void;
	page: number;
	setPage: (p: number) => void;
	perPage: number;
	/** Changes the results-per-page choice, resets page to 0, and persists the preference. */
	setPerPage: (n: number) => void;
	results: any | null;
	/** SSR/initial results; used so facets are not overwritten until user triggers a new search */
	initialResults: any | null;
	/** True while Algolia is fetching and there is no usable result set yet for the current view. */
	isSearchLoading: boolean;
	/** True when the last search attempt failed (backend unreachable, etc.) rather than genuinely returning zero hits. */
	searchError: boolean;
	refetch: () => Promise<void>;
  sort: 'relevance' | 'date' | 'salary';
  setSort: (s: 'relevance' | 'date' | 'salary') => void;
  // Helper to generate URL for a specific page
  getPageUrl: (targetPage: number) => string;
  // Map view support - removed mapBounds (not read by consumers, only setMapBounds is used)
  setMapBounds: (bounds: MapBounds | null) => void;
  viewMode: 'list' | 'map';
  setViewMode: (mode: 'list' | 'map') => void;
}

export const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export function SearchProvider({ children, indexName, initialQuery = '', initialFilters, initialPage = 0, initialPerPage, initialResults, syncToUrl = true, initialSort = 'relevance', initialViewMode = 'list', localeOverride }: SearchProviderProps & { initialViewMode?: 'list' | 'map' }) {
	// Get locale from page context (or override when outside Vike tree, e.g. SRP island)
	const pageContext = usePageContext();
	const locale = (pageContext?.locale ?? localeOverride ?? 'en') as string;
	const dispatch = useAppDispatch();
	const isRehydrated = useRehydrated();

	// Get Finland filter state from Redux (with fallback for SSR)
	// Use useSelector with a custom equality function to prevent SearchProvider from re-rendering
	// when Redux updates but the value hasn't actually changed. This is critical because Redux
	// store updates were causing SearchProvider to re-render, which then caused all consumers
	// (the SRP page, CustomHits) to re-render unnecessarily.
	// The equality function returns true if values are equal, preventing re-renders
	const isFinlandEnabled = useSelector(
		(state: RootState) => state.finlandFilter?.enabled ?? true,
		(a, b) => a === b // Strict equality - useSelector already does this by default, but explicit is clearer
	);
	// TEC-482 follow-up: gates buildFinlandBaselineFacetFilter's Finland-plus-remote-eligible
	// default (see finland-place-ids.ts) — starts false to match SSR, flips post-hydrate.
	const newLocationSystemEnabled = useNewLocationSystemEnabled();

  
	
	// Use a key to force re-initialization when initialResults change from null to actual data
	const providerKey = React.useMemo(() => {
		return `${indexName}-${initialQuery}-${JSON.stringify(initialFilters)}-${initialPage}-${!!initialResults}-${initialSort}`;
	}, [indexName, initialQuery, initialFilters, initialPage, initialResults, initialSort]);

	const searchClientRef = useRef<BrowserSearchClient | null>(null);
	const ensureSearchClient = useCallback(() => {
		if (typeof window === 'undefined') return null;
		if (searchClientRef.current) return searchClientRef.current;
		searchClientRef.current = createBrowserSearchClient();
		return searchClientRef.current;
	}, []);
	// Normalize provided index to the base (locale) index without any sort suffix
	const baseIndexName = useMemo(() => (indexName ? indexName.replace(/_(postedDate_desc|salaryRangeMin_desc)$/,'') : 'en_search') , [indexName]);

	const [query, setQuery] = useState<string>(initialQuery);
	const filtersRef = useRef<AlgoliaFilters>(initialFilters);
	const [page, setPage] = useState<number>(initialPage);
	const [filtersVersion, setFiltersVersion] = useState<number>(0);
	const filtersKeyRef = useRef<string>(filtersKey(initialFilters));
	const [results, setResults] = useState<any | null>(initialResults ?? null);
	// Use a ref to track current results for comparison in callbacks (avoids stale closures)
	const resultsRef = useRef<any | null>(initialResults ?? null);

	// Track hit IDs as a string to detect when actual content changes (not just reference)
	const hitIdsPrevRef = useRef<string>('');
	const hitIdsStableRef = useRef<string>('');
	const hitIds = useMemo(() => {
		const hits = results?.hits || [];
		const newHitIds = hits.map((hit: any) => hit?.objectID || hit?.id || '').join(',');
		const prevHitIds = hitIdsPrevRef.current;

		// If the value hasn't changed, return the previous stable reference
		if (prevHitIds === newHitIds && prevHitIds !== '') {
			return hitIdsStableRef.current;
		}

		hitIdsPrevRef.current = newHitIds;
		hitIdsStableRef.current = newHitIds;
		return newHitIds;
	}, [results?.hits]);

	// Track nbHits and page separately to catch other content changes
	const resultsContentKeyPrevRef = useRef<string>('');
	const resultsContentKeyStableRef = useRef<string>('');
	const resultsContentKey = useMemo(() => {
		const newKey = `${results?.nbHits || 0}-${results?.page || 0}-${hitIds}`;
		const prevKey = resultsContentKeyPrevRef.current;

		// If the key value hasn't changed, return the previous stable reference
		if (prevKey === newKey && prevKey !== '') {
			return resultsContentKeyStableRef.current;
		}

		resultsContentKeyPrevRef.current = newKey;
		resultsContentKeyStableRef.current = newKey;
		return newKey;
	}, [results?.nbHits, results?.page, hitIds]);

	// Keep ref in sync with state and track changes
	// Only run when resultsContentKey changes (actual content change, not just reference)
	const prevContentKeyRef = useRef<string>('');
	useEffect(() => {
		// Skip if content key hasn't actually changed (defensive check)
		// This handles the case where useMemo recalculates but produces the same value
		if (prevContentKeyRef.current === resultsContentKey && prevContentKeyRef.current !== '') {
			return;
		}

		// Always update the refs to keep them in sync
		resultsRef.current = results;
		prevContentKeyRef.current = resultsContentKey;
	}, [resultsContentKey]);

	const setResultsWithTracking = useCallback((newResults: any) => {
		setResults(newResults);
	}, []);
	// Separate results for map mode (geo search)
	// Initialize with SSR geo results if available and in map mode
	const [mapResults, setMapResults] = useState<any | null>(() => {
		// If we have initialResults and we're in map mode, use them as initial map results
		if (initialViewMode === 'map' && initialResults) {
			return initialResults;
		}
		return null;
	});
	
	// Helper to check if two results objects are effectively the same
	const areResultsEqual = useCallback((a: any, b: any): boolean => {
		if (a === b) return true;
		if (!a || !b) return a === b;
		
		// Compare key fields that matter for rendering
		if (a.nbHits !== b.nbHits) return false;
		if (a.page !== b.page) return false;
		if (a.nbPages !== b.nbPages) return false;
		
		// Compare hits array length and objectIDs (most efficient check)
		const hitsA = Array.isArray(a.hits) ? a.hits : [];
		const hitsB = Array.isArray(b.hits) ? b.hits : [];
		if (hitsA.length !== hitsB.length) return false;
		
		// Compare objectIDs to detect if hits actually changed
		for (let i = 0; i < hitsA.length; i++) {
			const idA = hitsA[i]?.objectID || hitsA[i]?.id;
			const idB = hitsB[i]?.objectID || hitsB[i]?.id;
			if (idA !== idB) return false;
		}
		
		return true;
	}, []);
	
	// Stable results reference - only update if results actually changed
	const stableResultsRef = useRef<any | null>(initialResults ?? null);
	// Track a version number that only increments when results actually change (not just reference)
	// This is used in the context value memo to prevent recalculation when only reference changes
	const [resultsVersion, setResultsVersion] = useState<number>(0);
	const resultsVersionRef = useRef<number>(0);
	// Track previous results reference for version tracking
	const prevResultsRefForVersion = useRef<any | null>(initialResults ?? null);
	
	// Compute effective results (no side effects in useMemo)
	const effectiveResults = useMemo(() => {
		const currentResults = results || initialResults;
		const previousResults = stableResultsRef.current;

		// Only update the stable reference if results actually changed
		if (areResultsEqual(previousResults, currentResults)) {
			return previousResults;
		}

		// Results actually changed - update stable reference (but don't set state here)
		stableResultsRef.current = currentResults;
		return currentResults;
	}, [results, initialResults, areResultsEqual]);

	// Update resultsVersion in an effect (not during render)
	// Track previous results reference to detect when it changes
	useEffect(() => {
		const currentResults = results || initialResults;
		const previousResults = stableResultsRef.current;
		const prevResultsRef = prevResultsRefForVersion.current;

		// Update version if:
		// 1. Results reference changed (new results object, even if content is same)
		// 2. Results content actually changed (different hit IDs, nbHits, etc.)
		// This ensures that when filters change and new results come in, we always update
		// even if the content appears the same (which shouldn't happen, but we want to be safe)
		const resultsRefChanged = prevResultsRef !== currentResults;
		const resultsContentChanged = !areResultsEqual(previousResults, currentResults);

		if (resultsRefChanged || resultsContentChanged) {
			resultsVersionRef.current += 1;
			setResultsVersion(resultsVersionRef.current);
			prevResultsRefForVersion.current = currentResults;
		}
	}, [results, initialResults, areResultsEqual]);
    const [sort, setSort] = useState<'relevance' | 'date' | 'salary'>(initialSort);
	const [perPage, setPerPageState] = useState<number>(
		isValidSrpHitsPerPage(initialPerPage) ? initialPerPage : SRP_HITS_PER_PAGE
	);
	const [isLoading, setIsLoading] = useState<boolean>(!initialResults);
	// True when the last search attempt threw a real error (backend unreachable, etc.), not an
	// expected/benign one (ad blocker, aborted request, see isExpectedEnvironmentError). Distinct
	// from `results === null`, which is ambiguous between "still loading" and "errored", SrpView
	// uses this to show an honest "search is temporarily unavailable" state instead of a silently
	// blank grid. Cleared on the next successful search.
	const [searchError, setSearchError] = useState<boolean>(false);

	// Store callbacks in refs to prevent context value recreation when they change
	// Initialize with placeholder functions that will be replaced after callbacks are defined
	const performSearchRef = useRef<() => Promise<void>>(async () => {});
	const performMapSearchRef = useRef<() => Promise<void>>(async () => {});
	const setFiltersCallbackRef = useRef<(f: AlgoliaFilters) => void>(() => {});
	const setSortWithTrackingRef = useRef<(s: 'relevance' | 'date' | 'salary') => void>(() => {});
	const setPerPageWithTrackingRef = useRef<(n: number) => void>(() => {});
	const getPageUrlRef = useRef<(targetPage: number) => string>(() => '#');
	
	const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
	// Normalize initialViewMode: 'grid' from the server (derived from the URL `view` param) is
	// treated as 'list' (responsive grid). This is the sole seed for viewMode, the URL is the
	// only source of truth, so server render and the client's first (hydration) render always
	// agree and there's nothing to reconcile post-hydration.
	const normalizedInitialViewMode: 'list' | 'map' = (initialViewMode === 'map' ? 'map' : 'list');
	const [viewMode, setViewModeState] = useState<'list' | 'map'>(normalizedInitialViewMode);

	const setViewMode = useCallback((mode: 'list' | 'map') => {
		setViewModeState(mode);
	}, []);
	
	// Automatically switch from 'map' to 'list' when on tools pages
	// This ensures map view is not available on tools pages
	// Note: Tools pages use their own local viewMode state, but this is a defensive measure
	// for any code that might use SearchContext's viewMode on tools pages
	useEffect(() => {
		// Skip on non-search pages
		if (!syncToUrl) return;
		if (typeof window === 'undefined') return;

		// Get current pathname from pageContext or window.location
		const currentPathname = pageContext?.urlPathname || pageContext?.urlParsed?.pathname || window.location.pathname;
		if (isToolsPage(currentPathname) && viewMode === 'map') {
			setViewMode('list');
		}
	}, [viewMode, setViewMode, pageContext, syncToUrl]);
	
	const didHydrateRef = useRef<boolean>(false);
	/**
	 * True after we've skipped the first client Algolia search because `initialResults` already
	 * contained SSR/prefetch hits. Must NOT set `didHydrateRef`, that ref is reserved for the URL-sync
	 * effect's first-run guard. If we flip `didHydrateRef` here, URL sync skips its safe early return,
	 * runs full `replaceState` with stale `query` from props (often "" on CSR), and wipes the real URL
	 * before `handleUrl` runs.
	 */
	const ssrInitialSearchHandledRef = useRef<boolean>(false);
	const suppressUrlSyncRef = useRef<boolean>(false);
    const prevQueryRef = useRef<string>(initialQuery || '');
    const prevFiltersRef = useRef<string>(JSON.stringify(initialFilters || {}));
    const prevSortRef = useRef<'relevance' | 'date' | 'salary'>(initialSort);
	const isSearchingRef = useRef<boolean>(false);
	// Store the SSR pathname to ensure hydration consistency
	const ssrPathnameRef = useRef<string | null>(null);

	// Simple in-memory cache for session (normal search)
	const cacheRef = useRef<Map<string, any>>(new Map());
	// Separate cache for map mode geo searches
	const mapCacheRef = useRef<Map<string, any>>(new Map());

	// Generate deterministic SRP ID from query + filters (+ an optional variant, e.g. page
	// number or map viewport, so paginating/panning to a genuinely different result set
	// produces a different id instead of being deduped away).
	const generateSrpId = useCallback((query: string, filters: AlgoliaFilters, variant?: string): string => {
		const filtersStr = typeof filters === 'string' ? filters : JSON.stringify(filters || {});
		const combined = `${query || ''}|${filtersStr}|${variant || ''}`;
		// Simple hash function for deterministic ID
		let hash = 0;
		for (let i = 0; i < combined.length; i++) {
			const char = combined.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32bit integer
		}
		return `srp_${Math.abs(hash).toString(36)}`;
	}, []);

	// Format filters for human-readable display
	const formatFiltersApplied = useCallback((filters: AlgoliaFilters): string => {
		if (!filters) return '';
		if (typeof filters === 'string') {
			// Try to parse if it's JSON
			try {
				const parsed = JSON.parse(filters);
				return Object.entries(parsed)
					.map(([key, value]) => {
						if (Array.isArray(value)) {
							return `${key}: ${value.join(', ')}`;
						}
						return `${key}: ${value}`;
					})
					.join('; ');
			} catch {
				return filters;
			}
		}
		return Object.entries(filters)
			.map(([key, value]) => {
				if (Array.isArray(value)) {
					return `${key}: ${value.join(', ')}`;
				}
				return `${key}: ${value}`;
			})
			.join('; ');
	}, []);

	// Track SRP view with debouncing
	const srpViewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastSrpIdRef = useRef<string | null>(null);

	/**
	 * Fire on every SRP result-set change (list or map mode): `srp_view` for the custom
	 * search-analytics stream, plus a typed `page_view` event so GA4 "Views" counts it too.
	 * SRP filter/query/sort/page/map-viewport changes update the URL via History API, not a
	 * Vike navigation, so `pages/+onPageTransitionEnd.ts` never runs for them, this is the
	 * only place those interactions get counted as a GA4 view. For Ahrefs specifically, only
	 * some of these changes need the same treatment: Ahrefs' script auto-tracks real
	 * `history.pushState` calls on its own, so a change that already went through pushState
	 * (see __ahrefsNativePageviewHref below) is skipped here to avoid double-counting; only
	 * `replaceState`-only changes get our own event translated into Ahrefs' pageview sentinel.
	 * `variant` distinguishes result sets that share the same query+filters but aren't the
	 * same view (pagination page number, map viewport) so paging/panning isn't deduped away.
	 */
	const trackSrpResultChange = useCallback((searchQuery: string, resultHitsCount: number | undefined, variant?: string) => {
		if (!didHydrateRef.current || typeof window === 'undefined') return;

		if (srpViewTimeoutRef.current) {
			clearTimeout(srpViewTimeoutRef.current);
		}

		srpViewTimeoutRef.current = setTimeout(() => {
			const srpId = generateSrpId(searchQuery, filtersRef.current, variant);

			// Only track if SRP ID changed (avoid duplicate tracking)
			if (srpId !== lastSrpIdRef.current) {
				// This URL change went through History API, not a Vike navigation, so +title.ts
				// never re-ran and document.title is still whatever it was at the last real page
				// load — keep it in sync so the browser tab and any GA4 page_view reading
				// document.title (see ga4.ts's page_view case) reflect the current filters/query.
				try {
					document.title = computeSearchPageTitleFromLiveState(
						window.location.href,
						locale as 'en' | 'fi' | 'sv',
						filtersRef.current,
						typeof resultHitsCount === 'number' ? resultHitsCount : null,
					);
				} catch {
					// Non-fatal: worst case the title stays stale, same as before this fix.
				}

				const filtersApplied = formatFiltersApplied(filtersRef.current);
				const locationLabel = typeof filtersRef.current === 'object' && filtersRef.current && 'location' in filtersRef.current
					? Array.isArray(filtersRef.current.location)
						? filtersRef.current.location[0]
						: String(filtersRef.current.location)
					: undefined;

				trackEvent({
					name: 'srp_view',
					params: {
						srp_id: srpId,
						query_text: searchQuery || undefined,
						location_label: locationLabel,
						result_count: resultHitsCount,
						filters_applied: filtersApplied || undefined,
						locale: locale,
					},
				});

				// Some SRP state changes reach this point via a real history.pushState call
				// (e.g. TopFilters pill clicks go through usePushIndexUiState, which pushState's
				// directly), Ahrefs' own script already auto-fires a native "pageview" for those,
				// tracked in __ahrefsNativePageviewHref by the pushState patch above. Only ones
				// that reached here via replaceState (invisible to Ahrefs' native tracking) need
				// our own event translated into Ahrefs' pageview sentinel, otherwise we'd send a
				// second, duplicate "pageview" for the same URL and inflate Ahrefs' Views metric.
				const ahrefsAlreadyCoveredNatively = (window as any).__ahrefsNativePageviewHref === window.location.href;

				trackEvent({
					name: 'spa_page_view',
					params: {
						page_type: 'srp',
						srp_id: srpId,
						locale: locale,
						ahrefs_send_as_pageview: !ahrefsAlreadyCoveredNatively,
					},
				});

				lastSrpIdRef.current = srpId;
			}
		}, 500); // 500ms debounce
	}, [generateSrpId, formatFiltersApplied, locale]);

	// Separate geo search function for map mode
	const performMapSearch = useCallback(async () => {
		// Only perform map search if in map mode and bounds exist
		if (viewMode !== 'map' || !mapBounds) {
			return;
		}

		// Prevent multiple simultaneous searches
		if (isSearchingRef.current) {
			return;
		}

		// Create a stable cache key for map search
		const currentCacheKey = JSON.stringify({ 
			indexName: baseIndexName, 
			query, 
			filters: filtersRef.current, 
			mapBounds,
			isFinlandEnabled,
			type: 'map'
		});
		
		// Serve from map cache if found
		if (mapCacheRef.current.has(currentCacheKey)) {
			const cachedResult = mapCacheRef.current.get(currentCacheKey);
			// Only update state if results actually changed
			if (!areResultsEqual(mapResults, cachedResult)) {
				setMapResults(cachedResult);
			}
			return;
		}

		isSearchingRef.current = true;
		setIsLoading(true);

		const client = ensureSearchClient();
		if (!client) {
			isSearchingRef.current = false;
			setIsLoading(false);
			return;
		}

		try {
			// Geo search params - only use insideBoundingBox for geo search
			const params: Record<string, any> = {
				query: query || '',
				page: 0, // Map search always starts at page 0
				hitsPerPage: SRP_MAP_SSR_GEO_HITS_PER_PAGE, // Typesense caps per_page at 250; stay in sync with the SSR map query
				clickAnalytics: true,
				// Facet counts are exact over ALL matching docs (not capped by hitsPerPage), so the
				// map can show accurate per-location density even when the pin sample is capped.
				// `locationList` (facetable array, unlike the joined `location` string) is what the
				// density counts are built from, so a job valid in multiple cities counts toward each
				// city instead of forming one combined "Espoo, Helsinki, Vantaa" bucket.
				// requiredLanguages powers the map's "Jobs in English" toggle (it needs the
				// locale-specific facet value, resolved from live facet data). geoCell
				// (utils/typesense/geo-cell.ts) is what the density bubble layer actually renders
				// from; regionAdmin1 is Finland's real named regions (maakunta) for the zoomed-out
				// view — see the SSR map query in pages/index/+data.ts for the full rationale.
				facets: ['locationList', 'requiredLanguages', 'geoCell', 'regionAdmin1'],
				// Finland has ~300 municipalities; the 100-value default truncated the density
				// layer's long tail, leaving most of the map dark outside the biggest cities.
				maxValuesPerFacet: 300,
				// Algolia insideBoundingBox format: [lat1, lng1, lat2, lng2]
				// We have: { north, south, east, west }
				// Convert to: [south, west, north, east] (southwest corner, then northeast corner)
				insideBoundingBox: [[mapBounds.south, mapBounds.west, mapBounds.north, mapBounds.east]],
			};

			// geoCellDistrict (~2km cells, the finest density tier) is only requested once the live
			// viewport is already small — faceting it over a wide-open bbox (the whole-Finland default,
			// or anything close to it) would compute a real cell->count breakdown across thousands of
			// cells nobody's about to look at yet, the same facet-compute cost concern documented on
			// the SSR map query in pages/index/+data.ts. ~1.5deg is roughly "a metro area and a bit of
			// slack", comfortably inside where the map's district tier (see MapView.tsx's tier zoom
			// thresholds) would actually render it.
			const DISTRICT_FACET_MAX_BOUNDS_SPAN_DEG = 1.5;
			const boundsSpanDeg = Math.max(mapBounds.north - mapBounds.south, mapBounds.east - mapBounds.west);
			if (boundsSpanDeg < DISTRICT_FACET_MAX_BOUNDS_SPAN_DEG) {
				params.facets = [...params.facets, 'geoCellDistrict'];
			}

			// Apply filters if any
			if (typeof filtersRef.current === 'string') {
				params.filters = filtersRef.current;
			} else if (filtersRef.current && typeof filtersRef.current === 'object' && filtersRef.current !== null && Object.keys(filtersRef.current).length > 0) {
				const facetFilters: (string | string[])[] = [];
				const numericFilters: string[] = [];
				const numericRule: Record<string, 'gte' | 'lte'> = {
					salaryRangeMin: 'gte',
					vacationWeeks: 'gte',
					companyHeadcountRangeMax: 'lte',
					lastSeenMin: 'gte',
				};
				
				Object.entries(filtersRef.current as Record<string, unknown>).forEach(([attribute, raw]) => {
					if (isSrpNonFilterQueryParam(attribute)) {
						return;
					}
					let values = Array.isArray(raw) ? raw as Array<string | number> : [raw as string | number];
					const isExclude = attribute.startsWith('exclude_');
					const actualAttribute = isExclude ? attribute.replace('exclude_', '') : attribute;
					
					if (numericRule[actualAttribute] && !isExclude) {
						values.forEach((v) => {
							const num = typeof v === 'number' ? v : Number(String(v).trim());
							const isValid = actualAttribute === 'lastSeenMin' 
								? (!Number.isNaN(num) && isFinite(num))
								: (!Number.isNaN(num) && isFinite(num) && num > 0);
							if (isValid) {
								const algoliaAttribute = refinementKeyToAlgoliaNumericAttribute(actualAttribute);
								const filterString = numericRule[actualAttribute] === 'gte' 
									? `${algoliaAttribute}>=${num}` 
									: `${algoliaAttribute}<=${num}`;
								numericFilters.push(filterString);
							}
						});
					} else if (!numericRule[actualAttribute]) {
						if (isExclude) {
							values.forEach((v) => {
								facetFilters.push(`NOT ${actualAttribute}:${String(v)}`);
							});
						} else {
							if (values.length === 1) {
								facetFilters.push(`${attribute}:${String(values[0])}`);
							} else if (values.length > 1) {
								facetFilters.push(values.map((v) => `${attribute}:${String(v)}`));
							}
						}
					}
				});
				
				// Add the Finland-baseline facet filter if enabled, unless an explicit non-Finland
				// location filter is active (that would AND against it and always yield 0 hits)
				if (isFinlandEnabled && !hasExplicitNonFinlandLocationFilter(filtersRef.current)) {
					facetFilters.push(buildFinlandBaselineFacetFilter(newLocationSystemEnabled));
				}

				if (facetFilters.length) params.facetFilters = facetFilters;
				if (numericFilters.length) params.numericFilters = numericFilters;
			} else {
				// Even if no other filters, apply the Finland baseline if enabled
				if (isFinlandEnabled) {
					params.facetFilters = [buildFinlandBaselineFacetFilter(newLocationSystemEnabled)];
				}
			}

			// "Trending" pulse: a second, hits-free query for the same viewport/filters but scoped to
			// recently-posted jobs. Comparing its per-location facet counts against the main query's
			// gives a real recent-vs-total ratio per city, no historical snapshot storage needed.
			const recentPostedDateThreshold = Date.now() - MAP_TRENDING_RECENT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
			const trendingParams: Record<string, any> = {
				...params,
				hitsPerPage: 1,
				numericFilters: [...(params.numericFilters || []), `postedDate>=${recentPostedDateThreshold}`],
			};
			const response = await searchWithFinlandBaselineFallback(
				client,
				[
					{ indexName: baseIndexName, params },
					{ indexName: baseIndexName, params: trendingParams },
				],
				newLocationSystemEnabled,
			);
			let result = response?.results?.[0] ?? null;
			const trendingResult = response?.results?.[1] ?? null;
			const recentLocationListFacet = (trendingResult as any)?.facets?.locationList;
			const recentGeoCellFacet = (trendingResult as any)?.facets?.geoCell;
			if (result && (recentLocationListFacet || recentGeoCellFacet)) {
				result = {
					...result,
					recentFacets: {
						...(recentLocationListFacet ? { locationList: recentLocationListFacet } : {}),
						...(recentGeoCellFacet ? { geoCell: recentGeoCellFacet } : {}),
					},
				};
			}
			if (result && typeof filtersRef.current === 'object' && filtersRef.current !== null) {
				const primary = readPrimaryLocationFilterValues(filtersRef.current as Record<string, unknown>);
				if (primary.length && Array.isArray(result.hits) && result.hits.length > 0) {
					const reordered = reorderSearchHitsForPrimaryLocationBoost(
						result.hits as Array<Record<string, unknown>>,
						primary,
						locale,
					);
					if (reordered !== result.hits) {
						result = { ...result, hits: reordered };
					}
				}
			}

			mapCacheRef.current.set(currentCacheKey, result);
			// Only update state if results actually changed
			if (!areResultsEqual(mapResults, result)) {
				setMapResults(result);
			}

			// Track SRP view + page_view (debounced to avoid excessive events).
			// Map mode previously had no analytics at all on filter/bounds changes.
			// Variant = viewport, so panning to a new area (same query/filters) still counts as a view.
			if (result) {
				trackSrpResultChange(query, (result as any)?.nbHits, mapBounds ? JSON.stringify(mapBounds) : undefined);
			}
		} catch (error) {
			if (!isExpectedEnvironmentError(error)) {
				console.error('[SearchProvider] Map geo search error', error);
				setMapResults(null);
			}
		} finally {
			setIsLoading(false);
			isSearchingRef.current = false;
		}
	}, [ensureSearchClient, baseIndexName, query, locale, mapBounds, viewMode, isFinlandEnabled, newLocationSystemEnabled, mapResults, areResultsEqual, trackSrpResultChange]);

	const performSearch = useCallback(async () => {
		// Skip normal search if in map mode
		if (viewMode === 'map') {
			return;
		}

		// Prevent multiple simultaneous searches
		if (isSearchingRef.current) {
			return;
		}

		// Create a stable cache key for this search (exclude mapBounds for normal search)
		const currentCacheKey = JSON.stringify({
			indexName: baseIndexName,
			query,
			filters: filtersRef.current,
			page,
			perPage,
			sort,
			isFinlandEnabled,
			type: 'normal'
		});
		
		// Serve from cache if found
		if (cacheRef.current.has(currentCacheKey)) {
			const cachedResult = cacheRef.current.get(currentCacheKey);
			// Only update state if results actually changed (use ref to avoid stale closure)
			const currentResults = resultsRef.current;
			if (!areResultsEqual(currentResults, cachedResult)) {
				setResultsWithTracking(cachedResult);
			}
			setSearchError(false);
			return;
		}

		isSearchingRef.current = true;
		setIsLoading(true);

		const client = ensureSearchClient();
		if (!client) {
			isSearchingRef.current = false;
			setIsLoading(false);
			return;
		}

		try {
			const params: Record<string, any> = {
				query: query || '',
				page: Math.max(0, Number.isFinite(page) ? page : 0),
				hitsPerPage: perPage,
				facets: [...FACET_ATTRIBUTES],
				clickAnalytics: true,
			};

			// Select index by sort preference starting from the normalized base index
			let effectiveIndexName = baseIndexName;
			if (sort === 'date') effectiveIndexName = `${baseIndexName}_postedDate_desc`;
			if (sort === 'salary') effectiveIndexName = `${baseIndexName}_salaryRangeMin_desc`;
			if (typeof filtersRef.current === 'string') {
				// Special handling: if filtering by objectID, prefer getObjects for exact match (objectID isn't facetable)
				const objectIdMatches = Array.from(filtersRef.current.matchAll(/objectID:\s*"([^"]+)"/g)).map(m => m[1]);
				if (objectIdMatches.length > 0) {
					try {
						const idx = (client as any).initIndex(effectiveIndexName);
						// @ts-ignore getObjects exists at runtime in algoliasearch/lite
						const res = await idx.getObjects(objectIdMatches);
						const hits = Array.isArray(res?.results) ? res.results.filter(Boolean) : [];

						const result = {
							hits,
							nbHits: hits.length,
							page: 0,
							nbPages: 1,
							hitsPerPage: hits.length,
							query: query || '',
							params: {},
						};
						cacheRef.current.set(currentCacheKey, result);
						// Only update state if results actually changed (use ref to avoid stale closure)
						const currentResults = resultsRef.current;
						if (!areResultsEqual(currentResults, result)) {
							setResultsWithTracking(result);
						}
						return;
					} catch (e) {
						// Fall back to normal search if getObjects fails
					}
				}
				params.filters = filtersRef.current;
			} else if (filtersRef.current && typeof filtersRef.current === 'object' && filtersRef.current !== null && Object.keys(filtersRef.current).length > 0) {
				// Split structured filters into facetFilters and numericFilters
				const facetFilters: (string | string[])[] = [];
				const numericFilters: string[] = [];
				const numericRule: Record<string, 'gte' | 'lte'> = {
					salaryRangeMin: 'gte',
					vacationWeeks: 'gte',
					companyHeadcountRangeMax: 'lte',
					lastSeenMin: 'gte',
				};

				Object.entries(filtersRef.current as Record<string, unknown>).forEach(([attribute, raw]) => {
					if (isSrpNonFilterQueryParam(attribute)) {
						return;
					}
					let values = Array.isArray(raw) ? raw as Array<string | number> : [raw as string | number];
					const isExclude = attribute.startsWith('exclude_');
					const isOnly = !isExclude && attribute.startsWith('only_');
					const actualAttribute = isExclude
						? attribute.replace('exclude_', '')
						: isOnly
							? attribute.replace('only_', '')
							: attribute;

					// Numeric filters cannot be excluded or "only"-filtered (they're range-based)
					if (numericRule[actualAttribute] && !isExclude && !isOnly) {
						values.forEach((v) => {
							// Convert to number, handling both string and number inputs
							const num = typeof v === 'number' ? v : Number(String(v).trim());
							// For posted-date window (key lastSeenMin), allow any finite number (timestamps can be large)
							// For other numeric filters, require > 0
							const isValid = actualAttribute === 'lastSeenMin' 
								? (!Number.isNaN(num) && isFinite(num))
								: (!Number.isNaN(num) && isFinite(num) && num > 0);
							if (isValid) {
								const algoliaAttribute = refinementKeyToAlgoliaNumericAttribute(actualAttribute);
								const filterString = numericRule[actualAttribute] === 'gte' 
									? `${algoliaAttribute}>=${num}` 
									: `${algoliaAttribute}<=${num}`;
								numericFilters.push(filterString);
							}
						});
					} else if (!numericRule[actualAttribute]) {
						// Check if this is an exclude or "only" filter
						if (isExclude) {
							// Algolia exclude syntax: NOT attribute:value
							values.forEach((v) => {
								facetFilters.push(`NOT ${actualAttribute}:${String(v)}`);
						});
					} else if (isOnly) {
							// Exact-match syntax: ONLY attribute:value (see build-typesense-search-params.ts)
							values.forEach((v) => {
								facetFilters.push(`ONLY ${actualAttribute}:${String(v)}`);
							});
					} else {
							// Regular include filter
						if (values.length === 1) {
							facetFilters.push(`${attribute}:${String(values[0])}`);
						} else if (values.length > 1) {
							facetFilters.push(values.map((v) => `${attribute}:${String(v)}`));
							}
						}
					}
				});
				
				// Add the Finland-baseline facet filter if enabled, unless an explicit non-Finland
				// location filter is active (that would AND against it and always yield 0 hits)
				if (isFinlandEnabled && !hasExplicitNonFinlandLocationFilter(filtersRef.current)) {
					facetFilters.push(buildFinlandBaselineFacetFilter(newLocationSystemEnabled));
				}

				if (facetFilters.length) params.facetFilters = facetFilters;
				if (numericFilters.length) {
					params.numericFilters = numericFilters;
				}
			} else {
				// Even if no other filters, apply the Finland baseline if enabled
				if (isFinlandEnabled) {
					params.facetFilters = [buildFinlandBaselineFacetFilter(newLocationSystemEnabled)];
				}
			}

			const response = await searchWithFinlandBaselineFallback(
				client,
				[{ indexName: effectiveIndexName, params }],
				newLocationSystemEnabled,
			);
			let result = response?.results?.[0] ?? null;
			if (result && typeof filtersRef.current === 'object' && filtersRef.current !== null) {
				const primary = readPrimaryLocationFilterValues(filtersRef.current as Record<string, unknown>);
				if (primary.length && Array.isArray(result.hits) && result.hits.length > 0) {
					const reordered = reorderSearchHitsForPrimaryLocationBoost(
						result.hits as Array<Record<string, unknown>>,
						primary,
						locale,
					);
					if (reordered !== result.hits) {
						result = { ...result, hits: reordered };
					}
				}
			}

			cacheRef.current.set(currentCacheKey, result);
			setSearchError(false);
			// Only update state if results actually changed (use ref to avoid stale closure)
			const currentResults = resultsRef.current;
			if (!areResultsEqual(currentResults, result)) {
				setResultsWithTracking(result);
			}
			
			// Track search query with results count
			if (query && didHydrateRef.current && result) {
				// Clear old query ID if this is a new search
				clearQueryID();
				// Use Algolia's queryID from search results if available
				const algoliaQueryID = (result as any)?.queryID || (result as any)?._queryID;
				trackSearchQuery({
					query,
					locale,
					hitsCount: (result as any)?.nbHits,
					queryID: algoliaQueryID,
				});

				trackSearchAnalyticsGa4Debounced({
					search_term: query,
					search_locale: locale,
					search_result_count: (result as any)?.nbHits ?? 0,
					search_has_results: ((result as any)?.nbHits ?? 0) > 0,
					search_filters: buildSearchFiltersJson(filtersRef.current),
					search_sort: resolveSearchSortLabel(sort),
				});
			}

			// Track SRP view + page_view (debounced to avoid excessive events).
			// Variant = page number, so paginating within the same query/filters still counts as a view.
			if (result) {
				trackSrpResultChange(query, (result as any)?.nbHits, `page:${page}`);
			}
		} catch (error) {
			// Flag the UI state regardless of whether this looks like an ad-blocker/offline blip or a
			// real backend outage, from the user's perspective both mean "results didn't load", and
			// SrpView uses this to show an honest message instead of a silently empty grid. Only the
			// logging/reporting stays gated on isExpectedEnvironmentError, same as before, to avoid
			// Sentry noise for client-side network noise that isn't actionable.
			setSearchError(true);
			if (!isExpectedEnvironmentError(error)) {
				console.error('[SearchProvider] Search error', error);
				setResultsWithTracking(null);
			}
		} finally {
			setIsLoading(false);
			isSearchingRef.current = false;
		}
	}, [ensureSearchClient, baseIndexName, page, perPage, query, sort, locale, isFinlandEnabled, newLocationSystemEnabled, viewMode, areResultsEqual, trackSrpResultChange]);

	// Track previous initialResults to prevent unnecessary updates
	const prevInitialResultsRef = useRef<any>(initialResults);
	
	// If initialResults exist, seed the cache for the initial key (base index + sort)
	useEffect(() => {
		// Skip cache seeding on non-search pages (but still allow state updates if needed)
		// On non-search pages, we don't need to cache results since we won't be searching
		if (!syncToUrl && !initialResults) {
			return;
		}

		// Only update if initialResults actually changed (deep comparison by checking nbHits and hits count)
		const prevResults = prevInitialResultsRef.current;
		const resultsChanged = 
			!prevResults && initialResults ? true : // Changed from null to results
			prevResults && !initialResults ? true : // Changed from results to null
			prevResults && initialResults && (
				prevResults.nbHits !== initialResults.nbHits ||
				(prevResults.hits?.length || 0) !== (initialResults.hits?.length || 0) ||
				prevResults.page !== initialResults.page
			);
		
		if (initialResults && resultsChanged) {
			// Only seed cache on search pages
			if (syncToUrl) {
				const initialKey = JSON.stringify({ indexName: baseIndexName, query: initialQuery || '', filters: initialFilters, page: initialPage, sort: initialSort });
				cacheRef.current.set(initialKey, initialResults);
			}
			// Only update state if results actually changed (compare with current results from ref to avoid dependency)
			// Use a ref to access current results without adding it to dependencies
			const currentResults = resultsRef.current;
			if (!areResultsEqual(currentResults, initialResults)) {
				setResultsWithTracking(initialResults);
			}
			setIsLoading(false);
			prevInitialResultsRef.current = initialResults;
		} else if (!initialResults && resultsChanged) {
			prevInitialResultsRef.current = null;
		}
		// Note: Removed 'results' from dependencies to prevent loop - we only want to react to initialResults changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialResults, areResultsEqual]);

	// Track previous isFinlandEnabled to detect changes
	const prevIsFinlandEnabledRef = useRef<boolean>(isFinlandEnabled);
	
	// Trigger search on state changes (but skip if results already cached)
	useEffect(() => {
		// Skip search when syncToUrl is false AND we have no filters (e.g. job detail / generic non-search pages).
		// Tools pages use syncToUrl=false but pass initialFilters (objectID: "id1" OR ...); they must still run search.
		const hasFilters =
			(typeof initialFilters === 'string' && initialFilters.length > 0) ||
			(initialFilters && typeof initialFilters === 'object' && Object.keys(initialFilters as object).length > 0);
		if (!syncToUrl && !hasFilters) {
			return;
		}

		// Skip initial search if we have SSR results and this is the initial state
		// In map mode, if we have SSR geo results, use them instead of searching
		if (initialResults && !ssrInitialSearchHandledRef.current) {
			ssrInitialSearchHandledRef.current = true;
			prevIsFinlandEnabledRef.current = isFinlandEnabled;
			
			// If in map mode and we have initial results, set them as map results
			if (viewMode === 'map' && initialResults) {
				// Set map results from SSR initial results immediately
				setMapResults(initialResults);
				// Cache the SSR results
				const cacheKey = JSON.stringify({
					indexName: baseIndexName,
					query,
					filters: filtersRef.current,
					mapBounds: null, // SSR results use default bounds
					isFinlandEnabled,
					type: 'map'
				});
				mapCacheRef.current.set(cacheKey, initialResults);
			}
			
			// For non-map mode, skip the search since we have SSR results
			if (viewMode !== 'map') {
				return;
			}
			// For map mode, we still want to search when bounds change, but we have initial results
		}
		
		// If isFinlandEnabled changed, clear cache entries that don't match the new state
		if (prevIsFinlandEnabledRef.current !== isFinlandEnabled) {
			// Clear all cache entries to force fresh search
			cacheRef.current.clear();
			mapCacheRef.current.clear();
			prevIsFinlandEnabledRef.current = isFinlandEnabled;
		}
		
		const timeoutId = setTimeout(() => {
			// Use refs to avoid including callbacks in dependencies (they're stable but references change)
			const currentPerformSearch = performSearchRef.current;
			const currentPerformMapSearch = performMapSearchRef.current;
			if (viewMode === 'map') {
				// Use geo search for map mode
				currentPerformMapSearch();
			} else {
				// Use normal search for grid/list mode
				currentPerformSearch();
			}
		}, 0);
		
		return () => clearTimeout(timeoutId);
		// Removed performSearch and performMapSearch from dependencies - using refs instead to prevent re-runs when callbacks are recreated
	}, [baseIndexName, page, perPage, query, sort, filtersVersion, mapBounds, viewMode, isFinlandEnabled, syncToUrl]);

  // React to changes in initialFilters prop (from parent pages) by updating filtersRef
  // Only update if filters actually changed (by content key, not just string representation)
  useEffect(() => {
    const nextKey = filtersKey(initialFilters);
    if (nextKey !== filtersKeyRef.current) {
      if (
        typeof window !== 'undefined' &&
        shouldIgnoreEmptyInitialFiltersFromLayout({
          syncToUrl,
          nextFiltersKey: nextKey,
          windowHref: window.location.href,
        })
      ) {
        return;
      }
      // Filters actually changed - update refs and version
      filtersKeyRef.current = nextKey;
      filtersRef.current = initialFilters;
      setFiltersVersion((v) => v + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters, syncToUrl]);

    // Reset page to 0 when query/filters/sort change AFTER initial hydration
    useEffect(() => {
		// Skip on non-search pages
		if (!syncToUrl) return;

        // If this change originated from a URL-driven update, don't force-reset page
        if (suppressUrlSyncRef.current) {
            return;
        }
        const currentFiltersStr = JSON.stringify(filtersRef.current || {});
        const queryChanged = prevQueryRef.current !== query;
        const filtersChanged = prevFiltersRef.current !== currentFiltersStr;
        const sortChanged = prevSortRef.current !== sort;
        if (didHydrateRef.current && (queryChanged || filtersChanged || sortChanged)) {
            setPage(0);
            // Also sync URL immediately so page param resets alongside state
            try {
                if (typeof window !== 'undefined') {
                    const uiState: any = {
                        query: query || '',
                        refinementList: typeof filtersRef.current === 'object' && filtersRef.current ? filtersRef.current : {},
                        page: 0, // 0-based: page 0 = first page
                        perPage,
                        sort,
                        isFinland: isFinlandEnabled,
                        view: viewMode === 'map' ? 'map' : undefined,
                    };
                    const url = generateUrlFromIndexUiState(
                      uiState,
                      baseIndexName,
                      window.location.pathname,
                      urlSyncOptionsForPathname(window.location.pathname),
                    );
                    const targetUrl = url && url.length > 0 ? url : window.location.pathname;
                    // Prevent URL sync effect from writing back immediately
                    suppressUrlSyncRef.current = true;
                    window.history.replaceState({}, '', targetUrl);
                    // Ensure any listeners react (in case history patch not yet applied)
                    try { window.dispatchEvent(new Event('urlchange')); } catch {}
                }
            } catch {}
        }
        // Don't update refs here - they're updated in the URL sync effect
        // This ensures the URL sync effect can detect changes before refs are updated
    }, [query, sort, filtersVersion, syncToUrl, baseIndexName, isFinlandEnabled, viewMode, perPage]);

	// Track last saved URL (and when) so quick refinements of the same in-progress search
	// coalesce into one history entry instead of one per settled query change.
	const lastSavedUrlRef = useRef<string | null>(null);
	const lastSavedAtRef = useRef<number>(0);
	const hasSavedInitialSearchRef = useRef<boolean>(false);

	// A save within this window of the previous auto-save (e.g. "head" -> "head of" -> "head
	// of engineering" typed in one sitting) updates that entry in place instead of adding a
	// new one; pausing longer than this starts a fresh saved-search entry.
	const SAVE_COALESCE_WINDOW_MS = 30000;

	/**
	 * Saves current search to "saved searches" (recent list) when:
	 * - Redux persist has rehydrated (isRehydrated), and
	 * - We have a meaningful search (non-empty query or any filters), and
	 * - The URL is different from the last saved.
	 *
	 * Triggers (all inside the URL-sync effect, client-only, syncToUrl true):
	 * 1. Initial load: first run after rehydration, if page has query or filters (e.g. landed on a search URL).
	 * 2. Suppress branch: user just changed query/filters/sort; the other effect set suppressUrlSyncRef and updated the URL, we save here.
	 * 3. Normal path: we're syncing the URL and isSearchChange && page === 0 && targetUrl !== lastSaved.
	 *
	 * Within SAVE_COALESCE_WINDOW_MS of the previous save, this updates that entry (via
	 * refineSearch) rather than adding a new one, otherwise every settled pause while typing
	 * a multi-word query (e.g. "head" / "head of" / "head of engineering") persists as its own
	 * separate saved-search entry.
	 */
	function trySaveCurrentSearchToRecent(filters: Record<string, unknown>, opts: { markInitial?: boolean }) {
		const hasQuery = query && String(query).trim().length > 0;
		const hasFilters = Object.keys(filters || {}).length > 0;
		if (!hasQuery && !hasFilters) return;
		try {
			const uiState: any = { query: query || '', refinementList: filters || {}, page: 0, perPage, sort, isFinland: isFinlandEnabled, view: viewMode === 'map' ? 'map' : undefined };
			const url = generateUrlFromIndexUiState(
				uiState,
				baseIndexName,
				window.location.pathname,
				urlSyncOptionsForPathname(window.location.pathname),
			);
			const targetUrl = url && url.length > 0 ? url : window.location.pathname;
			const fullUrl = normalizeSavedSearchUrlInput(window.location.origin + targetUrl);
			if (shouldSkipPersistingSavedSearchUrl(fullUrl)) return;
			if (fullUrl === lastSavedUrlRef.current) return;
			const now = Date.now();
			const isRefinement =
				!opts.markInitial &&
				lastSavedUrlRef.current != null &&
				now - lastSavedAtRef.current < SAVE_COALESCE_WINDOW_MS;
			if (isRefinement) {
				dispatch(refineSearch({ previousUrl: lastSavedUrlRef.current as string, url: fullUrl }));
			} else {
				dispatch(addSearch(fullUrl));
			}
			lastSavedUrlRef.current = fullUrl;
			lastSavedAtRef.current = now;
			if (opts.markInitial) hasSavedInitialSearchRef.current = true;
		} catch (e) {
			debugLog('[SearchContext] Failed to save recent search:', e);
		}
	}
	
	// Sync URL on state changes (client only)
	useEffect(() => {
		if (!syncToUrl || typeof window === 'undefined') return;
		// Wait for redux-persist rehydration so we don't get overwritten by REHYDRATE
		if (!isRehydrated) return;

		const currentFilters = typeof filtersRef.current === 'object' && filtersRef.current ? filtersRef.current : {};

		// First run after hydration: save initial search if page has query or filters
		if (!didHydrateRef.current) {
			didHydrateRef.current = true;
			trySaveCurrentSearchToRecent(currentFilters as Record<string, unknown>, { markInitial: true });
			return;
		}

		// User just changed query/filters/sort (other effect set suppressUrlSyncRef and updated URL)
		if (suppressUrlSyncRef.current) {
			suppressUrlSyncRef.current = false;
			trySaveCurrentSearchToRecent(currentFilters as Record<string, unknown>, {});
			return;
		}
		
		const currentFiltersStr = JSON.stringify(currentFilters);
		const queryChanged = prevQueryRef.current !== query;
		const filtersChanged = prevFiltersRef.current !== currentFiltersStr;
		const sortChanged = prevSortRef.current !== sort;
		const isSearchChange = queryChanged || filtersChanged || sortChanged;
		
		try {
			const uiState: any = {
				query: query || '',
				refinementList: currentFilters,
				page: Number.isFinite(page) ? page : 0,
				perPage,
				sort,
				isFinland: isFinlandEnabled,
				view: viewMode === 'map' ? 'map' : undefined,
			};
			const url = generateUrlFromIndexUiState(
				uiState,
				baseIndexName,
				window.location.pathname,
				urlSyncOptionsForPathname(window.location.pathname),
			);
			if (typeof window !== 'undefined' && typeof window.history !== 'undefined') {
				const targetUrl = url && url.length > 0 ? url : window.location.pathname;
				window.history.replaceState({}, '', targetUrl);
				window.dispatchEvent(new Event('urlchange'));
				if (isSearchChange && page === 0) {
					trySaveCurrentSearchToRecent(currentFilters as Record<string, unknown>, {});
				}
			}
			prevQueryRef.current = query;
			prevFiltersRef.current = currentFiltersStr;
			prevSortRef.current = sort;
		} catch (e) {
			try {
				const uiState: any = {
					query: query || '',
					refinementList: typeof filtersRef.current === 'object' && filtersRef.current ? filtersRef.current : {},
					page: Number.isFinite(page) ? page : 0,
					perPage,
					sort,
					isFinland: isFinlandEnabled,
					view: viewMode === 'map' ? 'map' : undefined,
				};
				const url =
					generateUrlFromIndexUiState(
						uiState,
						baseIndexName,
						window.location.pathname,
						urlSyncOptionsForPathname(window.location.pathname),
					) || (typeof window !== 'undefined' ? window.location.pathname : '');
				if (typeof window !== 'undefined' && typeof window.history !== 'undefined') {
					window.history.pushState({}, '', url);
				}
			} catch {}
		}
	}, [syncToUrl, query, page, perPage, baseIndexName, filtersVersion, sort, dispatch, isRehydrated, isFinlandEnabled, viewMode]);

	// React to URL changes by updating local state (popstate, SPA navigations)
	useEffect(() => {
		if (typeof window === 'undefined') return;
		// Respect pages that do not want URL-driven state
		if (!syncToUrl) return;
		// Patch history methods once to emit a custom event on SPA navigations
		if (!(history as any)._patchedForSearchProvider) {
			const origPush = history.pushState;
			const origReplace = history.replaceState;
			(history as any).pushState = function(this: History, ...args: any[]) {
				const ret = (origPush as any).apply(this, args as any);
				// Ahrefs' own script also patches pushState and auto-fires a native "pageview" for
				// any href change it produces (e.g. usePushIndexUiState's direct pushState call for
				// filter-pill clicks), record that so trackSrpResultChange doesn't send a second,
				// duplicate "pageview" for a URL Ahrefs already counted on its own. Stored on `window`
				// (not a React ref) so it survives SearchProvider unmounting/remounting, matching the
				// lifetime of this history patch itself (installed once per real page load).
				(window as any).__ahrefsNativePageviewHref = window.location.href;
				window.dispatchEvent(new Event('urlchange'));
				return ret;
			};
			(history as any).replaceState = function(this: History, ...args: any[]) {
				const ret = (origReplace as any).apply(this, args as any);
				window.dispatchEvent(new Event('urlchange'));
				return ret;
			};
			(history as any)._patchedForSearchProvider = true;
			// Ahrefs' script also auto-fires a native "pageview" once on initial script load, 
			// seed the tracker with the current href so the very first trackSrpResultChange call
			// (for the SSR-hydrated default view) doesn't duplicate that.
			(window as any).__ahrefsNativePageviewHref = window.location.href;
		}

		const handleUrl = (event?: Event) => {
			try {
				// Skip URL handling during initial hydration if we have SSR data
				// This prevents the URL parser from overriding SSR data
				if (initialResults && !didHydrateRef.current) {
					return;
				}

				const ui = parseUrlToIndexUiState(window.location.href);
				const nextQuery = ui.query || '';
				// URL uses 0-based indexing (page 0 = first page, page 1 = second page, etc.)
				const nextPage = Math.max(0, ui.page !== undefined && ui.page !== null ? ui.page : 0);
				const nextPerPage = isValidSrpHitsPerPage((ui as any).perPage) ? (ui as any).perPage : SRP_HITS_PER_PAGE;
				const nextFilters = (ui.refinementList || {}) as Record<string, string[]>;
				const parsedSort = (ui as any)?.sort as 'relevance' | 'date' | 'salary' | undefined;
				const nextSort: 'relevance' | 'date' | 'salary' =
					parsedSort === 'date' || parsedSort === 'salary' || parsedSort === 'relevance'
						? parsedSort
						: 'relevance';
				const nextIsFinland = (ui as any).isFinland !== false;
				if ((ui as any).isFinland !== undefined && nextIsFinland !== isFinlandEnabled) {
					dispatch(setFinlandFilterEnabled(nextIsFinland));
				}
				// The URL is the only source of truth for view mode, so a missing `view` param always
				// means 'list', on mount, popstate, and urlchange alike.
				const nextView: 'list' | 'map' = (ui as any).view === 'map' ? 'map' : 'list';
				if (nextView !== viewMode) {
					suppressUrlSyncRef.current = true;
					setViewMode(nextView);
				}
				// Only apply URL-derived state if it's different from current state
				// This prevents unnecessary state changes during initial hydration
				const currentQuery = query;
				const currentPage = page;
				const currentPerPage = perPage;
				const currentFiltersStr = JSON.stringify(filtersRef.current || {});
				const nextFiltersStr = JSON.stringify(nextFilters);
				const currentSort = sort;


				if (currentQuery !== nextQuery || currentPage !== nextPage || currentPerPage !== nextPerPage || currentFiltersStr !== nextFiltersStr || currentSort !== nextSort) {
					suppressUrlSyncRef.current = true;
					setQuery(nextQuery);
					setPage(nextPage);
					setPerPageState(nextPerPage);
					filtersRef.current = nextFilters;
					setFiltersVersion((v) => v + 1);
					setSort(nextSort);
				}
			} catch {}
		};

		window.addEventListener('popstate', handleUrl);
		window.addEventListener('urlchange', handleUrl);
		// Also run once on mount to reflect current URL
		handleUrl();
		return () => {
			window.removeEventListener('popstate', handleUrl);
			window.removeEventListener('urlchange', handleUrl);
		};
	}, [query, page, perPage, sort, syncToUrl, isFinlandEnabled, dispatch, viewMode, setViewMode]);

  const setSortWithTracking = useCallback((newSort: 'relevance' | 'date' | 'salary') => {
		trackSortChange({
			sortType: newSort,
			locale,
		});
		setSort(newSort);
	}, [locale]);

  const setPerPageWithTracking = useCallback((newPerPage: number) => {
		if (!isValidSrpHitsPerPage(newPerPage) || newPerPage === perPage) return;
		// Page boundaries shift when the page size changes, so go back to page 0.
		setPage(0);
		setPerPageState(newPerPage);
		if (typeof window !== 'undefined') {
			try { localStorage.setItem('perPage', String(newPerPage)); } catch {}
			setClientPreferenceCookie('perPage', String(newPerPage));
		}
	}, [perPage]);

  // Extract stable values from pageContext to prevent unnecessary recalculations
  const pageContextPathname = pageContext?.urlPathname;
  const pageContextOriginal = pageContext?.urlOriginal;
  
  // Normalize pathname to always include locale prefix for consistency between SSR and client
  // This must be the same on both server and client to prevent hydration mismatches
  // Store SSR pathname in ref during SSR, use it during client hydration
  // Use a ref to store the normalized pathname after initial hydration to prevent unnecessary recalculations
  const normalizedPathnameRef = useRef<string | null>(null);
  const normalizedPathname = useMemo(() => {
		// After initial hydration, use the stored value to prevent recalculation
		// Only recalculate if the actual pathname changed
		const currentPathname = pageContextPathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
		if (normalizedPathnameRef.current && didHydrateRef.current && normalizedPathnameRef.current.endsWith(currentPathname)) {
			return normalizedPathnameRef.current;
		}
		
		let rawPathname = '/';
		
		// On server, compute and store the normalized pathname
		if (typeof window === 'undefined') {
			if (pageContextPathname) {
				rawPathname = pageContextPathname;
			} else if (pageContextOriginal) {
				try {
					const url = new URL(pageContextOriginal, 'http://localhost');
					rawPathname = url.pathname;
				} catch {
					const match = String(pageContextOriginal).match(/^[^?]*/);
					rawPathname = match ? match[0] : '/';
				}
			}
			
			// Normalize and store for hydration
			const pathSegments = rawPathname.split('/').filter(Boolean);
			const firstSegment = pathSegments[0]?.toLowerCase();
			const hasLocalePrefix = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);
			
			if (!hasLocalePrefix && locale) {
				rawPathname = `/${locale}${rawPathname === '/' ? '' : rawPathname}`;
			}
			
			ssrPathnameRef.current = rawPathname;
			normalizedPathnameRef.current = rawPathname;
			return rawPathname;
		}
		
		// On client during initial hydration, use stored SSR pathname to ensure consistency
		// After hydration completes, we can use the current pathname
		if (ssrPathnameRef.current && !didHydrateRef.current) {
			normalizedPathnameRef.current = ssrPathnameRef.current;
			return ssrPathnameRef.current;
		}
		
		// After hydration, use pageContext or window location (only calculate once)
		if (pageContextPathname) {
			rawPathname = pageContextPathname;
		} else if (typeof window !== 'undefined') {
			rawPathname = window.location.pathname;
		}
		
		// Normalize pathname to always include locale prefix
		const pathSegments = rawPathname.split('/').filter(Boolean);
		const firstSegment = pathSegments[0]?.toLowerCase();
		const hasLocalePrefix = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);
		
		if (!hasLocalePrefix && locale) {
			rawPathname = `/${locale}${rawPathname === '/' ? '' : rawPathname}`;
		}
		
		// Store for future renders
		normalizedPathnameRef.current = rawPathname;
		return rawPathname;
	}, [
		pageContextPathname, // Primary source (consistent on both server and client)
		pageContextOriginal, // Server fallback
		locale,
	]);

  // Generate URL for a specific page number
  const getPageUrl = useCallback((targetPage: number): string => {
		try {
			const uiState: any = {
				query: query || '',
				refinementList: typeof filtersRef.current === 'object' && filtersRef.current ? filtersRef.current : {},
				page: targetPage, // Use 0-based for URLs (page 0 = first page, page 1 = second page, etc.)
				perPage,
				sort,
				isFinland: isFinlandEnabled,
				view: viewMode === 'map' ? 'map' : undefined,
			};

			// Use normalized pathname that's consistent between SSR and client
			const url = generateUrlFromIndexUiState(
				uiState,
				baseIndexName,
				normalizedPathname,
				urlSyncOptionsForPathname(normalizedPathname),
			);
			return url || '#';
		} catch (e) {
			return '#';
		}
	}, [query, perPage, sort, baseIndexName, normalizedPathname, viewMode]);

  // Memoize filters object to avoid recreating it on every render
  // Use a stable empty object to prevent reference changes
  const emptyFiltersObject = useMemo(() => ({}), []);
  const filtersObject = useMemo(() => {
		return (typeof filtersRef.current === 'object' && filtersRef.current) ? filtersRef.current : emptyFiltersObject;
	}, [filtersVersion, emptyFiltersObject]);

  // Memoize setFilters callback to avoid recreating it on every render
  const setFiltersCallback = useCallback((newFilters: AlgoliaFilters) => {
		// Check if filters actually changed (by content, not just reference)
		const nextKey = filtersKey(newFilters);
		if (nextKey === filtersKeyRef.current) {
			// No semantic change → do nothing
			return;
		}

		// Track filter changes
		const oldFilters = filtersRef.current;
		const oldFiltersObj = typeof oldFilters === 'object' && oldFilters ? oldFilters : {};
		const newFiltersObj = typeof newFilters === 'object' && newFilters ? newFilters : {};
		
		// Track added filters
		Object.entries(newFiltersObj).forEach(([key, values]) => {
			const oldValues = oldFiltersObj[key];
			const newValues = Array.isArray(values) ? values : [values];
			const oldValuesArray = Array.isArray(oldValues) ? oldValues : oldValues ? [oldValues] : [];
			
			newValues.forEach(value => {
				if (!oldValuesArray.includes(value as string)) {
					trackFilterAdd({
						filterKey: key,
						filterValue: String(value),
						locale,
					});
				}
			});
		});
		
		// Track removed filters
		Object.entries(oldFiltersObj).forEach(([key, values]) => {
			const newValues = newFiltersObj[key];
			const oldValuesArray = Array.isArray(values) ? values : [values];
			const newValuesArray = Array.isArray(newValues) ? newValues : newValues ? [newValues] : [];
			
			oldValuesArray.forEach(value => {
				if (!newValuesArray.includes(value as string)) {
					trackFilterRemove({
						filterKey: key,
						filterValue: String(value),
						locale,
					});
				}
			});
		});
		
		// Update filters and version only when content actually changed
		filtersKeyRef.current = nextKey;
		filtersRef.current = newFilters;
		setFiltersVersion((v) => v + 1);
		// Let the URL sync effect handle URL updates
		// Kick off search immediately
		void performSearch();
	}, [locale, performSearch]);

	// Update callback refs after all callbacks are defined
	performSearchRef.current = performSearch;
	performMapSearchRef.current = performMapSearch;
	setFiltersCallbackRef.current = setFiltersCallback;
	setSortWithTrackingRef.current = setSortWithTracking;
	setPerPageWithTrackingRef.current = setPerPageWithTracking;
	getPageUrlRef.current = getPageUrl;

  // Group basic primitive state into one object so the value useMemo below can depend on a
  // single stable reference instead of five separate primitives.
  const basicState = useMemo(() => {
    return { query, page, perPage, sort, viewMode };
  }, [query, page, perPage, sort, viewMode]);

  // Use a ref to store the previous context value and only create a new one if values actually changed
  const prevValueRef = useRef<SearchContextValue | null>(null);
  const prevDepsRef = useRef<Record<string, any>>({});
  // Track previous resultsVersion to detect when it changes (must be outside useMemo)
  const prevResultsVersionRef = useRef<number>(0);
  const value = useMemo<SearchContextValue>(() => {
		// Compute effectiveResults inside this useMemo to avoid dependency on the effectiveResults useMemo
		// This prevents unnecessary recalculations when results/initialResults change but are effectively equal
		const currentResults = results || initialResults;
		const previousResults = stableResultsRef.current;

		// Check if resultsVersion changed BEFORE computing stableEffectiveResults
		// This ensures we always use new results when version changes (e.g., after filter changes)
		const resultsVersionChanged = prevResultsVersionRef.current !== resultsVersion;

		let stableEffectiveResults = previousResults;

		// If resultsVersion changed, always use the new results (even if content appears same)
		// This is critical for filter changes - we need to trigger re-renders even if results look similar
		if (resultsVersionChanged) {
			stableResultsRef.current = currentResults;
			stableEffectiveResults = currentResults;
		} else {
			// Only update the stable reference if results actually changed
			// This is critical - we want to keep the same reference to prevent unnecessary re-renders
			if (!areResultsEqual(previousResults, currentResults)) {
				// Content actually changed - update the stable reference
				stableResultsRef.current = currentResults;
				stableEffectiveResults = currentResults;
			} else {
				// Content is the same - keep using the previous stable reference to prevent re-renders
				// Even if currentResults is a new reference, we use previousResults (stableResultsRef.current)
				stableEffectiveResults = previousResults;
				// Update the ref to the new reference for future comparisons, but don't use it in context value
				if (previousResults !== currentResults && currentResults !== null) {
					stableResultsRef.current = currentResults;
				}
			}
		}

		// Read callbacks from refs to avoid including them in dependencies
		const currentPerformSearch = performSearchRef.current || performSearch;
		const currentPerformMapSearch = performMapSearchRef.current || performMapSearch;
		const currentSetFiltersCallback = setFiltersCallbackRef.current || setFiltersCallback;
		const currentSetSortWithTracking = setSortWithTrackingRef.current || setSortWithTracking;
		const currentSetPerPageWithTracking = setPerPageWithTrackingRef.current || setPerPageWithTracking;
		const currentGetPageUrl = getPageUrlRef.current || getPageUrl;

		// Use mapResults when in map mode, otherwise use normal results
		const effectiveResultsForContext = basicState.viewMode === 'map' ? mapResults : stableEffectiveResults;
		const refetchFunction = viewMode === 'map' ? currentPerformMapSearch : currentPerformSearch;

		const newValue: SearchContextValue = {
			// Removed indexName - not used by consumers
			query: basicState.query,
			setQuery,
			filters: filtersObject,
			setFilters: currentSetFiltersCallback,
			page: basicState.page,
			setPage,
			perPage: basicState.perPage,
			setPerPage: currentSetPerPageWithTracking,
			results: effectiveResultsForContext,
			initialResults: initialResults ?? null,
			isSearchLoading: isLoading,
			searchError,
			refetch: refetchFunction,
			sort: basicState.sort,
			setSort: currentSetSortWithTracking,
			getPageUrl: currentGetPageUrl,
			// Removed mapBounds - not read by consumers (only setMapBounds is used)
			setMapBounds,
			viewMode: basicState.viewMode,
			setViewMode,
		};

		// Compare with previous value - if nothing meaningful changed, return the previous value
		const prevValue = prevValueRef.current;

		if (prevValue) {
			// Check if any meaningful values changed (ignore callback references)
			// Callbacks are always new references but their behavior is stable
			const queryChanged = prevValue.query !== newValue.query;
			// Use deep equality for filters to avoid false positives from reference changes
			const filtersChanged = (() => {
				const prev = prevValue.filters;
				const curr = newValue.filters;
				if (prev === curr) return false;
				if (typeof prev !== 'object' || typeof curr !== 'object' || prev === null || curr === null) return true;
				const prevKeys = Object.keys(prev);
				const currKeys = Object.keys(curr);
				if (prevKeys.length !== currKeys.length) return true;
				return prevKeys.some(k => prev[k] !== curr[k]);
			})();
			const pageChanged = prevValue.page !== newValue.page;
			const perPageChanged = prevValue.perPage !== newValue.perPage;
			// Use deep equality for results to avoid false positives from reference changes
			// Results objects can have new references but same content (from SearchContext updates)
			// IMPORTANT: If resultsVersion changed, we should always consider results changed
			// This ensures that when filters change and new results come in, components re-render
			const resultsChanged = (() => {
				// If resultsVersion changed, always consider results changed (even if content appears same)
				// This is critical for filter changes - new results should trigger re-renders
				if (resultsVersionChanged) {
					return true;
				}
				const prev = prevValue.results;
				const curr = newValue.results;
				if (prev === curr) return false;
				if (!prev || !curr) return prev !== curr;
			// Use areResultsEqual to check if content is actually different
			return !areResultsEqual(prev, curr);
			})();
			const sortChanged = prevValue.sort !== newValue.sort;
			const viewModeChanged = prevValue.viewMode !== newValue.viewMode;
			const mapResultsChanged = (basicState.viewMode === 'map' && mapResults !== prevValue.results);
			const isSearchLoadingChanged = prevValue.isSearchLoading !== newValue.isSearchLoading;
			const searchErrorChanged = prevValue.searchError !== newValue.searchError;

			const hasMeaningfulChange =
				queryChanged ||
				filtersChanged ||
				pageChanged ||
				perPageChanged ||
				resultsChanged ||
				sortChanged ||
				viewModeChanged ||
				mapResultsChanged ||
				isSearchLoadingChanged ||
				searchErrorChanged ||
				resultsVersionChanged; // Always update if resultsVersion changed

			// If nothing meaningful changed, return the previous value to prevent unnecessary re-renders
			if (!hasMeaningfulChange) {
				prevValueRef.current = prevValue;
				// Don't update prevResultsVersionRef - version hasn't changed meaningfully
				return prevValue;
			} else {
				// There was a meaningful change - return newValue
				// Update prevResultsVersionRef if version changed (for accurate tracking on next render)
				if (resultsVersionChanged) {
					prevResultsVersionRef.current = resultsVersion;
				}
				prevValueRef.current = newValue;
				return newValue;
			}
		}

		// If no previous value, this is the first render - return new value
		prevValueRef.current = newValue;
		return newValue;
	}, [basicState, resultsVersion, filtersObject, initialResults, mapResults, areResultsEqual, results, isLoading, searchError, setQuery, setPage, setMapBounds, setViewMode]);

	return (
		<SearchContext.Provider value={value}>{children}</SearchContext.Provider>
	);
}

export function useSearchContext(): SearchContextValue {
	const ctx = useContext(SearchContext);
	if (!ctx) {
		throw new Error('useSearchContext must be used within a SearchProvider');
	}
	return ctx;
}

/** Returns the search context or undefined when not inside SearchProvider (e.g. SRP island before interactive tree mounts). */
export function useSearchContextOptional(): SearchContextValue | undefined {
	return useContext(SearchContext);
}

export function useSearchResults() {
	const { results } = useSearchContext();
	return { results };
}

export function useSearchLoading(): boolean {
	return useSearchContext().isSearchLoading;
}

/** True when the last client-side search attempt failed (see SearchContextValue.searchError). */
export function useSearchError(): boolean {
	return useSearchContext().searchError;
}

export function useSearchQuery() {
	const { query, setQuery } = useSearchContext();
	return { query, setQuery };
}

export function useSearchFilters() {
	const { filters, setFilters } = useSearchContext();
	return { filters, setFilters };
}

export function useSearchPage() {
	const { page, setPage } = useSearchContext();
	return { page, setPage };
}

export function useSearchPerPage() {
	const { perPage, setPerPage } = useSearchContext();
	return { perPage, setPerPage };
}

export function useSearchSort() {
  const { sort, setSort } = useSearchContext();
  return { sort, setSort };
}
